import { FC, useState } from 'react';
import styled from 'styled-components';

import color from 'atoms/colors/colors';
import shadow from 'atoms/shadows/shadows';

import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

import type { ContentfulInPageFeatures } from 'graphqlTypes';

const InPageFeaturesV3Desktop: FC<ContentfulInPageFeatures> = ({ inPageNavigationItems }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Container id="features-v3" className="max-w-[1320px] mx-auto my-0">
      <div className="flex justify-between mx-auto mt-8 mb-0 items-center h-[540px]">
        <div className="nav-items flex flex-wrap justify-between gap-5 h-max">
          {inPageNavigationItems?.map((item, index) => {
            const icon = item?.icon?.bynderImageDesktop || item?.icon?.imageDesktop;

            return (
              <div
                className={`nav-item nav-item-${index} ${
                  activeIndex === index ? 'active' : ''
                } flex p-5 gap-5 rounded-[16px] border-2 border-solid border-transparent`}
                onClick={() => setActiveIndex(index)}
                key={`nav-item-${index}`}
                role="presentation"
              >
                {icon && <Image image={icon as ImageData} alt="Nav icon" />}
                <div className="nav-item-heading">
                  {item?.title && (
                    <div className="nav-item-title">
                      <Text as="h3" textStyle="textMd" fontWeight="bold" textColor="gray-900">
                        {item.title}
                      </Text>
                    </div>
                  )}
                  {item?.excerpt && (
                    <div className="nav-item-sub-title">
                      <Text as="p" textStyle="textXs" textColor="gray-700">
                        {richTextParser(item.excerpt)}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="nav-featured-content-container justify-center flex-col items-center text-center">
          {inPageNavigationItems?.map((item, index) => {
            const featuredImage = item?.featuredImage?.bynderImageDesktop || item?.featuredImage?.imageDesktop;

            return (
              <div
                className={`nav-featured-content ${activeIndex === index ? 'active' : ''}`}
                key={`featured-${index}`}
              >
                {featuredImage && <Image image={featuredImage as ImageData} alt="featured" />}
                {item?.description && richTextParser(item.description)}
                {item?.link && <ComponentButton label="Learn More" link={item?.link} />}
              </div>
            );
          })}
        </div>
      </div>
    </Container>
  );
};

export default InPageFeaturesV3Desktop;

export const Container = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 20px;
    margin-bottom: 10px;
  }

  .nav-item {
    border: 1px solid transparent;
    h3 {
      font-size: 16px;
      margin-bottom: 5px;
    }

    img {
      width: 100%;
      max-width: 30px;
      height: max-content;
      max-height: 30px;
    }

    &:hover {
      cursor: pointer;
      border: 1px solid ${color.navy[300]};
      background-color: ${color.navy[50]}80;
    }
  }

  .nav-item.active {
    background: white;
    box-shadow: ${shadow.xl};
    border: 1px solid ${color.gray[50]};
  }

  .nav-featured-content-container {
    p {
      margin-top: 15px;
      font-size: 16px;
    }

    .gatsby-image-wrapper,
    img {
      max-width: 510px;
      max-height: 338px;
      object-fit: contain !important;
      margin: 0 auto 30px auto;
    }

    .MuiButton-root {
      margin: 25px auto 0 auto;
    }
  }

  .nav-featured-content {
    display: none;

    &.active {
      display: block;
    }
  }

  @media (min-width: 576px) {
    .nav-featured-content-container,
    .nav-items {
      width: 100%;
      max-width: calc(50% - 15px);
    }

    .nav-item {
      max-width: calc((100% - 20px) / 2);
    }
  }

  @media (min-width: 1320px) {
    .nav-featured-content-container,
    .nav-items {
      max-width: calc((100% - 100px) / 2);
    }
  }

  @media (max-width: 1100px) {
    .nav-featured-content-container {
      img {
        max-width: 100%;
        max-height: 338px;
        margin-bottom: 30px;
      }
    }
  }
`;
